/**
 * AgentOutput envelope (Milestone A — P0-3 / P0-4; canonical 09 §7;
 * technical/07 §Envelope Schema Contract, version `agent_output_envelope/v1`).
 *
 * The envelope a BOXED agent returns. It is a forward-compatible **superset** of
 * the built `skillOutputContractSchema` (see `runtime.ts`): every new field is
 * `.optional()` / `.default()`-ed so a v0.1 simple-task output still validates as
 * a zero-proposal envelope — exactly how `contextPacketV2Schema` supersets the
 * v0.1 packet.
 *
 * Per technical/07 the schema is **strict**: unknown top-level fields reject
 * unless a package opts into the explicit extension namespace key
 * (`agent_output_envelope/v1`). The degenerate built contract
 * (`{result_summary, confidence, next_step, needs_clarification}`) is NOT parsed
 * directly against this strict schema — `normalizeToEnvelope` lifts that legacy
 * shape into the envelope's vocabulary (`result_summary -> summary`) before parse.
 *
 * BOXED doctrine (DECISIONS-LOCKED ledger J): an agent only ever *proposes*. The
 * envelope carries proposals (claims / evidence / transitions / decisions /
 * action cards / tool calls / events); the `AgentOutputValidator` turns the valid
 * ones into candidates via DomainServices / the ToolBroker policy path. Nothing
 * here mutates state or emits events. `tool_call_requests[].sanitized_payload` is
 * secret-scanned BEFORE the ToolBroker sees it (technical/07 sub-item invariants).
 */

import { z } from 'zod';

import {
  AGENT_OUTPUT_RUN_STATUS,
  CLAIM_TYPES,
  EVENT_TYPES,
  EVIDENCE_POLARITY,
} from '../constants/enums';
import { sanitizedJsonTextSchema } from './_sanitized-json';

// --- Proposal sub-shapes (09 §7) -------------------------------------------

/** A claim the agent proposes; the validator converts it via `ClaimService.create`. */
export const proposedClaimSchema = z.object({
  claim_type: z.enum(CLAIM_TYPES),
  subject_type: z.string(),
  subject_id: z.string(),
  statement: z.string(),
  confidence: z.number().min(0).max(1).optional(),
});
export type ProposedClaim = z.infer<typeof proposedClaimSchema>;

/**
 * A piece of evidence the agent proposes against a (proposed or existing) claim.
 * `claim_ref` is the index/id of a proposed claim in `proposed_claims` OR an
 * existing `claim_id`; the validator resolves it before calling
 * `EvidenceService.create`.
 */
export const proposedEvidenceSchema = z.object({
  claim_ref: z.string(),
  source_type: z.string(),
  source_id: z.string(),
  polarity: z.enum(EVIDENCE_POLARITY),
  interpretation: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
});
export type ProposedEvidence = z.infer<typeof proposedEvidenceSchema>;

/** A phase transition the agent proposes; recorded as a `transition_proposals` row. */
export const transitionProposalSchema = z.object({
  artifact_id: z.string(),
  contour_id: z.string(),
  to_phase: z.string(),
  rationale: z.string().optional(),
});
export type TransitionProposalProposal = z.infer<typeof transitionProposalSchema>;

/** A decision the agent requests; converted via `DecisionService.requestDecision`. */
export const decisionRequestSchema = z.object({
  artifact_id: z.string().optional(),
  title: z.string(),
  decision_type: z.string().optional(),
  rationale: z.string().optional(),
});
export type DecisionRequestProposal = z.infer<typeof decisionRequestSchema>;

/**
 * A follow-up event the agent proposes. Realized by the runtime through the
 * owning DomainService — the validator NEVER emits a raw agent event itself.
 */
export const proposedEventSchema = z.object({
  event_type: z.enum(EVENT_TYPES),
  subject_type: z.string().optional(),
  subject_id: z.string().optional(),
  reason: z.string().optional(),
});
export type ProposedEvent = z.infer<typeof proposedEventSchema>;

/**
 * A tool call the agent requests (technical/07 line 56). Routed through the
 * ToolBroker **policy path** — never invoked directly. `sanitized_payload` is the
 * JSON-text command body and MUST pass secret scanning (no `api_key` /
 * `authorization` / `secret` keys) before the ToolBroker is reached; the shared
 * `sanitizedJsonTextSchema` enforces that at parse time so a secret cannot enter
 * the envelope at all.
 */
export const toolCallRequestSchema = z.object({
  tool_name: z.string(),
  capability: z.string(),
  subject_type: z.string(),
  subject_id: z.string(),
  request_hash: z.string(),
  sanitized_payload: sanitizedJsonTextSchema,
});
export type ToolCallRequest = z.infer<typeof toolCallRequestSchema>;

/**
 * An action card the agent requests (technical/07 line 55). Realized by the
 * Action Card service. `suggested_effect_hash` references a pre-hashed effect the
 * card would dispatch; `priority` orders it on the surface.
 */
export const actionCardRequestSchema = z.object({
  subject_type: z.string(),
  subject_id: z.string(),
  capability: z.string(),
  title: z.string(),
  body: z.string(),
  suggested_effect_hash: z.string().optional(),
  priority: z.number().optional(),
});
export type ActionCardRequest = z.infer<typeof actionCardRequestSchema>;

// --- The envelope (09 §7; technical/07 §Envelope Schema Contract) ----------

export const agentOutputEnvelopeSchema = z
  .object({
    run_status: z.enum(AGENT_OUTPUT_RUN_STATUS).default('completed'),
    summary: z.string().default(''),
    confidence: z.number().min(0).max(1).default(0),
    needs_human_review: z.boolean().default(false),
    output_artifacts: z.array(z.unknown()).default([]),
    proposed_events: z.array(proposedEventSchema).default([]),
    proposed_claims: z.array(proposedClaimSchema).default([]),
    proposed_evidence: z.array(proposedEvidenceSchema).default([]),
    transition_proposals: z.array(transitionProposalSchema).default([]),
    decision_requests: z.array(decisionRequestSchema).default([]),
    action_card_requests: z.array(actionCardRequestSchema).default([]),
    tool_call_requests: z.array(toolCallRequestSchema).default([]),
    errors: z.array(z.string()).default([]),
    warnings: z.array(z.string()).default([]),
    // The ONLY sanctioned escape hatch for package-specific top-level data
    // (technical/07 §Envelope Schema Contract). A package opts into this
    // versioned extension namespace instead of scattering unknown top-level
    // keys; everything else rejects under `.strict()`.
    'agent_output_envelope/v1': z.record(z.string(), z.unknown()).optional(),
  })
  // STRICT: unknown top-level fields reject (K10 / technical/07). Forward-compat
  // for the degenerate built contract is handled by `normalizeToEnvelope` lifting
  // `result_summary -> summary` BEFORE parse, not by tolerating unknown keys.
  .strict();
export type AgentOutputEnvelope = z.infer<typeof agentOutputEnvelopeSchema>;

// --- Normalization (09 §7) -------------------------------------------------

/** The four keys that identify a built `skillOutputContractSchema` object. */
interface LegacySkillOutput {
  result_summary: string;
  confidence: number;
  next_step: string;
  needs_clarification: boolean;
}

/**
 * Detect a built simple-task contract (`skillOutputContractSchema`) so it can be
 * lifted into the envelope shape. Kept structural (not a `safeParse` against the
 * runtime schema) so this module has no dependency cycle with `runtime.ts`.
 */
function isLegacySkillOutput(raw: unknown): raw is LegacySkillOutput {
  if (raw === null || typeof raw !== 'object') return false;
  const obj = raw as Record<string, unknown>;
  return (
    typeof obj.result_summary === 'string' &&
    typeof obj.confidence === 'number' &&
    typeof obj.next_step === 'string' &&
    typeof obj.needs_clarification === 'boolean'
  );
}

/**
 * Result of `normalizeToEnvelope`: either a parsed envelope or a structured
 * failure (non-JSON / wrong shape) the validator maps to `failed_schema`.
 */
export type NormalizeResult =
  | { ok: true; envelope: AgentOutputEnvelope }
  | { ok: false; errors: string[] };

/**
 * Normalize a raw agent output into an `AgentOutputEnvelope`.
 *
 * 1. A built simple-task contract is lifted into a **zero-proposal** envelope
 *    (`result_summary -> summary`, `needs_clarification -> needs_human_review`),
 *    keeping the v0.1 slice validating as `accepted` with zero produced records.
 * 2. Otherwise the raw object is parsed with `agentOutputEnvelopeSchema`.
 *
 * A non-object / schema-invalid input returns `{ ok: false, errors }` so the
 * validator records `failed_schema` without throwing.
 */
export function normalizeToEnvelope(raw: unknown): NormalizeResult {
  if (isLegacySkillOutput(raw)) {
    const parsed = agentOutputEnvelopeSchema.safeParse({
      run_status: 'completed',
      summary: raw.result_summary,
      confidence: clamp01(raw.confidence),
      needs_human_review: raw.needs_clarification,
    });
    if (!parsed.success) {
      return { ok: false, errors: flattenZodErrors(parsed.error) };
    }
    return { ok: true, envelope: parsed.data };
  }

  if (raw === null || typeof raw !== 'object') {
    return { ok: false, errors: ['agent output was not a JSON object'] };
  }

  const parsed = agentOutputEnvelopeSchema.safeParse(raw);
  if (!parsed.success) {
    return { ok: false, errors: flattenZodErrors(parsed.error) };
  }
  return { ok: true, envelope: parsed.data };
}

/** Clamp a confidence into [0,1] so an out-of-range legacy value still parses. */
function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** Flatten a ZodError into a compact string list for the validation outcome. */
function flattenZodErrors(error: z.ZodError): string[] {
  return error.issues.map((issue) => {
    const path = issue.path.join('.');
    return path ? `${path}: ${issue.message}` : issue.message;
  });
}
